import {
  Image,
  ImageProps,
  Text,
  TextProps,
  Group,
  GroupProps,
  Box,
  Skeleton,
} from '@mantine/core';
import cn from 'clsx';
import { useAccount } from 'wagmi';
import { BigNumber } from 'bignumber.js';
import { BiSolidWallet } from 'react-icons/bi';
import { useTokenBalance } from 'src/api/smart-contracts';
import { useTokensInfo, useTokensPrices } from 'src/api/dex';
import { NETWORK_VARS } from 'src/constants';
import { TokenInfo } from 'src/types';
import { formatDecimal } from 'src/utils';
import { FallbackTokenIcon } from './FallbackTokenIcon';

type AddressOrSymbol =
  | {
      address: string;
      symbol?: string;
    }
  | {
      address?: string;
      symbol: string;
    };

export function Token() {
  return null;
}

const getTokenImgUrl = (symbol?: string, tokenInfo?: TokenInfo) => {
  if (symbol === '__APRMON__') {
    return '/tokens/APRMON.svg';
  }
  if (symbol === '__MON__') {
    return '/tokens/MON.svg';
  }

  return tokenInfo?.logo_url;
};

function TokenIcon({
  address,
  symbol,
  size = 18,
  imageProps,
  className,
}: AddressOrSymbol & {
  size?: number;
  imageProps?: ImageProps;
  className?: string;
}) {
  const { getTokenByAddress, getTokenBySymbol, isLoadingTokensInfo } =
    useTokensInfo();

  let tokenInfo = address
    ? getTokenByAddress(address)
    : getTokenBySymbol(symbol!);

  const imgUrl = getTokenImgUrl(
    address === NETWORK_VARS.tokensAddresses.MON ? '__MON__' : symbol,
    tokenInfo
  );

  return (
    <Box w={size} h={size} style={{ flexShrink: 0 }} className={className}>
      {isLoadingTokensInfo ? (
        <Skeleton
          w={size}
          h={size}
          style={{ flexShrink: 0, borderRadius: '50%' }}
        />
      ) : imgUrl ? (
        <Image
          src={imgUrl}
          w={size}
          h={size}
          {...imageProps}
          style={{ flexShrink: 0, borderRadius: '50%', ...imageProps?.style }}
        />
      ) : (
        <FallbackTokenIcon width={size} height={size} />
      )}
    </Box>
  );
}

Token.Icon = TokenIcon;

function TokenIconPair({
  token0,
  token1,
  size,
}: {
  token0: AddressOrSymbol;
  token1: AddressOrSymbol;
  size?: number;
}) {
  return (
    <Group gap={0}>
      <TokenIcon
        {...token0}
        size={size}
        className="z-20 outline outline-2 outline-[#181d22]"
      />
      <TokenIcon
        {...token1}
        size={size}
        className="ml-[-8px] outline outline-2 outline-[#181d22]"
      />
    </Group>
  );
}

Token.IconPair = TokenIconPair;

function TokenName({
  address,
  symbol,
  textProps,
}: AddressOrSymbol & {
  textProps?: TextProps;
}) {
  const { getTokenByAddress, getTokenBySymbol } = useTokensInfo();

  const tokenInfo = address
    ? getTokenByAddress(address)
    : getTokenBySymbol(symbol!);

  return <Text {...textProps}>{tokenInfo?.name ? tokenInfo?.name : '--'}</Text>;
}

Token.Name = TokenName;

function TokenSymbol({
  address,
  symbol,
  textProps,
}: AddressOrSymbol & {
  textProps?: TextProps;
}) {
  const { getTokenByAddress, getTokenBySymbol } = useTokensInfo();

  const tokenInfo = address
    ? getTokenByAddress(address)
    : getTokenBySymbol(symbol!);

  return (
    <Text {...textProps}>{tokenInfo?.symbol ? tokenInfo?.symbol : '--'}</Text>
  );
}

Token.Symbol = TokenSymbol;

function TokenSymbolPair({
  token0,
  token1,
  textProps,
}: {
  token0: AddressOrSymbol;
  token1: AddressOrSymbol;
  textProps?: TextProps;
}) {
  const { getTokenByAddress, getTokenBySymbol } = useTokensInfo();

  const token0Info = token0.address
    ? getTokenByAddress(token0.address)
    : getTokenBySymbol(token0.symbol!);
  const token1Info = token1.address
    ? getTokenByAddress(token1.address)
    : getTokenBySymbol(token1.symbol!);

  return (
    <Text {...textProps}>
      {token0Info?.symbol ? token0Info?.symbol : '--'} /{' '}
      {token1Info?.symbol ? token1Info?.symbol : '--'}
    </Text>
  );
}

Token.SymbolPair = TokenSymbolPair;

function TokenBalance({
  tokenInfo,
  useSCBalance = true,
  value: valueFromProps,
  isLoading,
  underline = false,
  withSymbol = true,
  withIcon = true,
  textProps,
  groupProps,
  emptyIfZero = false,
  color = '#7E7E95',
  onClick,
}: {
  tokenInfo?: Partial<TokenInfo>;
  useSCBalance?: boolean;
  value?: string | BigNumber | number;
  isLoading?: boolean;
  underline?: boolean;
  withSymbol?: boolean;
  withIcon?: boolean;
  textProps?: TextProps;
  groupProps?: GroupProps;
  emptyIfZero?: boolean;
  color?: string;
  onClick?: (value: string) => void;
}) {
  const { address } = useAccount();

  const tokenAddress = tokenInfo?.contract_address;

  const { balance, isLoadingBalance } = useTokenBalance({
    tokenAddress,
    enabled: useSCBalance && !!tokenInfo,
  });

  if (!tokenInfo || !address) {
    return null;
  }

  if (isLoading || (useSCBalance && isLoadingBalance)) {
    return <Skeleton width={100} height={18} />;
  }

  const handleClick = (value: number) => {
    onClick?.(
      BigNumber(balance?.value ?? 0)
        .times(BigNumber(value).div(100))
        .div(10 ** (Number(tokenInfo?.decimals) ?? 18))
        .toFixed(Number(tokenInfo?.decimals) ?? 18, BigNumber.ROUND_DOWN)
    );
  };

  const amountToDisplay =
    (useSCBalance ? balance?.formatted : valueFromProps) ?? 0;

  if (emptyIfZero && BigNumber(amountToDisplay).isZero()) {
    return null;
  }

  return (
    <Group gap={6} {...groupProps}>
      {withIcon && <BiSolidWallet size={16} color={color} />}

      <Text
        c={color}
        fz={14}
        td={underline ? 'underline' : 'none'}
        {...textProps}
        className={cn('flex items-center gap-2', textProps?.className)}
      >
        {formatDecimal(amountToDisplay, { withTruncate: true })}{' '}
        {withSymbol && tokenInfo?.symbol}
      </Text>

      {onClick
        ? [{ value: 100, label: 'Max' }].map(({ value, label }) => {
            return (
              <Group
                key={value}
                className="cursor-pointer px-3 h-[26px] bg-secondarybrand/10 rounded-[10px]"
                onClick={() => handleClick(value)}
              >
                <Text fz={14} fw={700} variant="gradient">
                  Max
                </Text>
              </Group>
            );
          })
        : null}
    </Group>
  );
}

Token.Balance = TokenBalance;

function TokenPrice({
  tokenInfo,
  amount,
  price: priceFromProps,
  isLoading: isLoadingFromProps,
  showBalance = false,
  emptyIfZero = false,
  textProps,
}: {
  tokenInfo?: TokenInfo;
  amount?: string;
  price?: string;
  isLoading?: boolean;
  showBalance?: boolean;
  emptyIfZero?: boolean;
  textProps?: TextProps;
}) {
  const { address } = useAccount();

  const { balance, isLoadingBalance } = useTokenBalance({
    tokenAddress: tokenInfo?.contract_address,
    enabled: showBalance && !!tokenInfo,
  });

  const priceApiEnabled =
    !priceFromProps && !isLoadingFromProps && !!tokenInfo?.contract_address;

  const { tokensPrices, isLoadingTokensPrices, tokensPricesError } =
    useTokensPrices({ enabled: priceApiEnabled });

  /* const { data: priceData, error: priceError } = useQuery({
    queryKey: ['tokenPrice', tokenInfo?.contract_address],
    queryFn: (): Promise<{ address: string; usdPrice: string }> =>
      doFetch(
        `${DEX_API_URL}/prices/${tokenInfo?.contract_address?.toLowerCase()}`
      ),
    enabled: priceApiEnabled,
  }); */

  if (!tokenInfo) {
    return null;
  }

  if (
    isLoadingFromProps ||
    isLoadingTokensPrices ||
    (showBalance && isLoadingBalance)
  ) {
    return <Skeleton width={50} height={18} />;
  }

  if ((!showBalance && !amount) || (showBalance && !address)) {
    return null;
  }

  const amountToDisplay = showBalance
    ? (balance?.formatted ?? 0)
    : (amount ?? 0);
  const price = priceFromProps ?? tokensPrices?.[tokenInfo.contract_address];

  if (emptyIfZero && BigNumber(amountToDisplay).isZero()) {
    return null;
  }

  return (
    <Text fz={14} fw={500} c="#7E7E95" {...textProps}>
      {priceApiEnabled && tokensPricesError ? (
        '--'
      ) : (
        <>
          $
          {formatDecimal(
            BigNumber(price ?? 0)
              .times(amountToDisplay)
              .toFixed(),
            { withTruncate: true }
          )}
        </>
      )}
    </Text>
  );
}

Token.Price = TokenPrice;
